import { getCookie, setCookie } from 'typescript-cookie';
import { CookieAttributes } from "typescript-cookie/dist/types";
import StorageTypes from "./storage/Types";

const cookieParams: CookieAttributes = {
	secure: true,
	sameSite: "None",
	expires: 14,
	path: "/",
}
const WHITELISTED_KEYS = [
	'API_URL',
	'SAML_COOKIE',
	'SAML_ALIAS',
	'authorization',
	'user',
];

export default class {
	storageType: StorageTypes;

	constructor(storageType: StorageTypes) {
		this.storageType = storageType;
	}

	setFromObject(o: any) {
		for(const key in o) {
			this.set(key, o[key])
		}
	}

	set(key: string, value: any) {
		switch(this.storageType) {
			case StorageTypes.cookie:
				setCookie(key, JSON.stringify(value), cookieParams);
				break;
			case StorageTypes.localStorage:
			case StorageTypes.sessionStorage:
				this.getSessionOrLocalStorage().setItem(key, JSON.stringify(value));
				break;
			default:
				console.error(`Storage type "${this.storageType}" isn't handled`);
		}
	}

	clear() {
		switch(this.storageType) {
			case StorageTypes.cookie:
				const data = this.getCookieData();

				for(const key in data) {
					this.delete(key);
				}

				break
			case StorageTypes.localStorage:
			case StorageTypes.sessionStorage:
				this.getSessionOrLocalStorage().clear();
				break
			default:
				console.error(`Storage type "${this.storageType}" isn't handled`);
		}
	}

	getAll(): {[key: string]: any} {
		switch(this.storageType) {
			case StorageTypes.cookie:
				return this.getCookieData();
			case StorageTypes.localStorage:
			case StorageTypes.sessionStorage:
				const storage = this.getSessionOrLocalStorage();
				const r: {[key: string]: any} = {};

				for (let i = 0; i < storage.length; i++) {
					const key = storage.key(i);

					if(key !== null && WHITELISTED_KEYS.indexOf(key) !== -1) {
						const value = storage.getItem(key);

						if(value !== null) {
							// Object.defineProperty(r, key)
							r[key] = JSON.parse(value);
						}
					}
				}

				return r;
			default:
				console.error(`Storage type "${this.storageType}" isn't handled`);
		}

		return {}
	}

	get(key: string): any {
		switch(this.storageType) {
			case StorageTypes.cookie:
				return this.getCookieData(key);
			case StorageTypes.localStorage:
			case StorageTypes.sessionStorage:
				const value = this.getSessionOrLocalStorage().getItem(key)

				return value === null ? null : JSON.parse(value);
			default:
				console.error(`Storage type "${this.storageType}" isn't handled`);
		}
	}

	delete(key: string) {
		switch(this.storageType) {
			case StorageTypes.cookie:
				this.set(key, "");
				break;
			case StorageTypes.localStorage:
			case StorageTypes.sessionStorage:
				this.getSessionOrLocalStorage().removeItem(key);
				break;
			default:
				console.error(`Storage type "${this.storageType}" isn't handled`);
		}
	}

	getSessionOrLocalStorage() {
		// Are we in a browser environment?
		if(globalThis.window) {
			return this.storageType === StorageTypes.localStorage ? localStorage : sessionStorage
		}

		// Fake it for node environment
		return {
			length: 0,
			setItem: (key: any, value: any) => console.info(`Fake setting ${key} to ${value}`),
			clear: () => {},
			key: () => {return ""; },
			getItem: (key: any) => {
				console.info(`Fake getting ${key}, returning ""`)
				return "";
			},
			removeItem: (key: any) => console.info(`Fake removing ${key}`),
		};
	}

	private getCookieData(key?: string): {[key: string]: any} {
		const r: {[key: string]: any} = {}

		if(
			document.cookie.length === 0 ||
			(key && WHITELISTED_KEYS.indexOf(key) === -1)
		) {
			return r;
		}

		if(key) {
			Object.assign(r, this.getSingleCookie(key));
		} else {
			WHITELISTED_KEYS.forEach(key => {
				const d = this.getSingleCookie(key);

				if(d !== undefined) {
					Object.assign(r, { [key]: d })
				}
			});
		}

		return r
	}

	private getSingleCookie(key: string): any {
		const value = getCookie(key);

		if(value === undefined) {
			return undefined;
		}

		return typeof value === 'string' && value.length > 0 ? JSON.parse(value) : value;
	}
}
